//@ts-nocheck
import { useContext, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { Box, Typography } from '@mui/material';
import {
  CaretRight,
  EyeSlash,
  Flag,
  Info,
  Lightbulb,
  Path,
  Prohibit,
  Tag
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { XGButton, XGChip } from 'xg-cl';
import * as PropTypes from 'prop-types';
import { addTravelDays, formatTravelDate } from '../../../../utils/travelDate';
import { deleteTrip } from '../../../../services/trip/trip';
import { ErrorToast } from '../../../../utils/alerts';
import DeleteModal from '../../DeleteModal/DeleteModal';
import './TripImportSummary.css';

const SOURCE_LABEL_KEYS = {
  text: 'tripImport.summary.sourceOutline',
  pdf: 'tripImport.summary.sourcePdf',
  docx: 'tripImport.summary.sourceDocument',
  url: 'tripImport.summary.sourceLink'
};

// The decisions are listed by where the operator would change them, in trip order (the backend already sorts them the
// same way; this keeps the groups right if an older draft arrives out of order).
const AREA_ORDER = [
  'overview',
  'itinerary',
  'infoDocs',
  'amenities',
  'pricing'
];

/** The trip page place (and its `?tab=` / `sub` / `day` deep link) a decision belongs to. */
const getDecisionDestination = (note) => {
  const area = AREA_ORDER.includes(note?.area) ? note.area : 'overview';
  switch (area) {
    case 'itinerary':
      return note.dayNumber
        ? {
            labelKey: 'tripImport.summary.destinations.itineraryDay',
            day: note.dayNumber,
            search: `tab=itinerary&day=${note.dayNumber}`
          }
        : {
            labelKey: 'tripImport.summary.destinations.itinerary',
            search: 'tab=itinerary'
          };
    case 'infoDocs':
      return {
        labelKey: 'tripImport.summary.destinations.infoDocs',
        search: 'tab=infoDocs&sub=content'
      };
    case 'amenities':
      return {
        labelKey: 'tripImport.summary.destinations.amenities',
        search: 'tab=infoDocs&sub=amenities'
      };
    case 'pricing':
      return {
        labelKey: 'tripImport.summary.destinations.pricing',
        search: 'tab=pricing'
      };
    default:
      return {
        labelKey: 'tripImport.summary.destinations.overview',
        search: 'tab=overview'
      };
  }
};

/** Groups decisions by area in trip order, keeping each group's own order (which is already by day). */
const groupDecisionsByArea = (notes) =>
  AREA_ORDER.map((area) => ({
    area,
    items: notes.filter(
      (note) =>
        (AREA_ORDER.includes(note?.area) ? note.area : 'overview') === area
    )
  })).filter((group) => group.items.length > 0);

const getSourceLabel = (t, kind) =>
  t(SOURCE_LABEL_KEYS[kind] || SOURCE_LABEL_KEYS.text);

/**
 * Joins the non-zero parts of a stat card headline with " · ", falling back
 * to a "nothing here" message when every part is zero (e.g. no pricing plans
 * and no add-ons at all).
 */
const joinHeadlineParts = (t, parts) =>
  parts.length ? parts.join(' · ') : t('tripImport.summary.nothingAdded');

/**
 * V3 read-only, post-creation "Here's what I did" summary, shown after the
 * trip has already been created from a `TripImportDraft`.
 *
 * @param {Object} props
 * @param {Object} props.draft
 * @param {number} props.tripId
 * @param {() => void} [props.onDiscarded] - Called after the trip was
 *   deleted, so the caller can close the modal and reset its own state.
 * @param {'created'|'reopened'} [props.mode] - `reopened` shows the stored
 *   summary inside the trip (no Discard / Go to trip footer).
 * @param {boolean} [props.showLinks] - Show each decision's link to its place
 *   on the trip (off where the trip page has no tab to open, see Trip.jsx).
 */
/** "$5,499" when the plan states a currency code, otherwise just the grouped number. */
const formatPlanPrice = (plan) => {
  const amount = Number(plan.supplierPrice);
  if (plan.currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: plan.currency,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2
      }).format(amount);
    } catch {
      return `${plan.currency} ${amount}`;
    }
  }
  return amount.toLocaleString('en-US');
};

const TripImportSummary = ({ draft, tripId, onDiscarded, mode, showLinks }) => {
  const isReopened = mode === 'reopened';
  const { t } = useTranslation(['trip']);
  const navigate = useNavigate();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  const trip = draft?.trip || {};
  const counts = draft?.counts || {};
  const pricingPlans = draft?.pricingPlans || [];
  const addOns = draft?.addOns || [];
  const notes = draft?.notes || [];
  const notImported = draft?.notImported || [];
  const noteGroups = groupDecisionsByArea(notes);

  const destinationLabel =
    trip.destination?.title ||
    trip.destination?.query ||
    t('tripImport.summary.noDestination');

  const dateRangeLabel =
    trip.startDate && trip.duration
      ? `${formatTravelDate(trip.startDate)} – ${formatTravelDate(
          addTravelDays(trip.startDate, trip.duration - 1)
        )}`
      : t('tripImport.summary.noDatesYet');

  const subtitle = t('tripImport.summary.subtitle', {
    source: getSourceLabel(t, draft?.source?.kind),
    title: trip.title,
    days: t('tripImport.summary.daysCount', { count: trip.duration || 0 })
  });

  const includedCount = draft?.amenities?.included?.length || 0;
  const notIncludedCount = draft?.amenities?.notIncluded?.length || 0;
  const amenitiesCount = includedCount + notIncludedCount;
  const additionalContentCount = draft?.additionalContent?.length || 0;

  const itineraryParts = [];
  if (counts.days) {
    itineraryParts.push(
      t('tripImport.summary.daysCount', { count: counts.days })
    );
  }
  if (counts.services) {
    itineraryParts.push(
      t('tripImport.summary.servicesCount', { count: counts.services })
    );
  }
  const itineraryHeadline = joinHeadlineParts(t, itineraryParts);

  const infoDocsParts = [];
  if (amenitiesCount) {
    infoDocsParts.push(
      t('tripImport.summary.amenitiesCount', { count: amenitiesCount })
    );
  }
  if (additionalContentCount) {
    infoDocsParts.push(
      t('tripImport.summary.infoCount', { count: additionalContentCount })
    );
  }
  const infoDocsHeadline = joinHeadlineParts(t, infoDocsParts);

  const pricingPlansCount = counts.pricingPlans ?? pricingPlans.length;
  const pricingParts = [];
  if (pricingPlansCount) {
    pricingParts.push(
      t('tripImport.summary.plansCount', { count: pricingPlansCount })
    );
  }
  if (counts.addOns) {
    pricingParts.push(
      t('tripImport.summary.addOnsCount', { count: counts.addOns })
    );
  }
  const pricingHeadline = joinHeadlineParts(t, pricingParts);
  const addOnNamesLine = addOns.map((addOn) => addOn.title).join(', ');

  // Links carry the operator's public prices: their plans are created visible at the stated price. Documents get
  // hidden $0 placeholders, so only those keep the placeholder note.
  const isLinkSource = draft?.source?.kind === 'url';
  // Same rule as the backend: a price is created visible only when it is in the trip currency (or states none),
  // otherwise the plan is kept hidden at $0. Read from the store when there is one; without a known trip currency
  // there is nothing to compare against, so the stated price is shown.
  const reduxContext = useContext(ReactReduxContext);
  const baseCurrency =
    reduxContext?.store?.getState?.()?.tenant?.basecurrency?.currencycode;
  const isPriced = (plan) =>
    Number(plan.supplierPrice) > 0 &&
    (!plan.currency ||
      !baseCurrency ||
      String(plan.currency).toUpperCase() ===
        String(baseCurrency).toUpperCase());
  // A stored summary records what the import did with each plan (`visible`), so the reopened view reports exactly
  // that. A plan without it is unknown (an older record): it is counted as neither priced nor hidden. The post-import
  // summary has no stored outcome, so it applies the backend's rule to the draft.
  const planOutcome = (plan) => {
    if (typeof plan.visible === 'boolean') return plan.visible;
    return isReopened ? null : isPriced(plan);
  };
  const pricedPlansLine = isLinkSource
    ? pricingPlans
        .filter(
          (plan) => planOutcome(plan) === true && Number(plan.supplierPrice) > 0
        )
        .map((plan) => `${plan.name} ${formatPlanPrice(plan)}`)
        .join(' · ')
    : '';
  const unpricedPlanCount = isLinkSource
    ? pricingPlans.filter((plan) => planOutcome(plan) === false).length
    : 0;
  let pricingPlansNote = '';
  if (isLinkSource) {
    if (unpricedPlanCount > 0) {
      pricingPlansNote = t('tripImport.summary.plansHiddenNote', {
        count: unpricedPlanCount
      });
    }
  } else if (pricingPlansCount > 0) {
    pricingPlansNote = t('tripImport.summary.pricingPlansPlaceholderNote');
  }

  const handleGoToDecision = (note) => {
    if (!tripId) {
      return;
    }
    navigate(`/trips/${tripId}?${getDecisionDestination(note).search}`);
  };

  const handleGoToTrip = () => {
    if (!tripId) {
      return;
    }
    navigate(`/trips/${tripId}`);
  };

  const handleConfirmDiscard = async () => {
    setIsDiscarding(true);
    try {
      await deleteTrip(tripId);
      setIsDiscardModalOpen(false);
      onDiscarded?.();
    } catch (err) {
      ErrorToast(t('tripImport.errors.discardFailed'));
    } finally {
      setIsDiscarding(false);
    }
  };

  const cards = [
    {
      key: 'overview',
      Icon: Flag,
      label: t('overview'),
      headline: (
        <Box className='trip-import-summary-card-headline-row'>
          <Typography className='trip-import-summary-card-headline trip-import-summary-card-headline--clamp'>
            {trip.title}
          </Typography>
        </Box>
      ),
      sublines: [
        <Box
          key='overview-sub'
          className='trip-import-summary-card-subline-row'
        >
          <Typography className='trip-import-summary-card-subline'>
            {destinationLabel} {'\u00b7'} {dateRangeLabel}
          </Typography>
          {trip.descriptionGenerated && (
            <XGChip
              id='trip-import-generated-chip'
              size='small'
              variant='outlined'
              text={t('tripImport.summary.generatedChip')}
              className='trip-import-summary-generated-chip'
            />
          )}
        </Box>
      ]
    },
    {
      key: 'itinerary',
      Icon: Path,
      label: t('itinerary'),
      headline: itineraryHeadline,
      sublines:
        counts.images > 0
          ? [
              t('tripImport.summary.imagesSubline', {
                count: counts.images
              })
            ]
          : []
    },
    {
      key: 'infoDocs',
      Icon: Info,
      label: t('infoAndDocs'),
      headline: infoDocsHeadline,
      sublines: [
        t('tripImport.summary.infoDocsSubline', {
          included: draft?.amenities?.included?.length || 0,
          notIncluded: draft?.amenities?.notIncluded?.length || 0
        })
      ]
    },
    {
      key: 'pricing',
      Icon: Tag,
      label: t('pricing'),
      headline: pricingHeadline,
      sublines: [addOnNamesLine, pricedPlansLine, pricingPlansNote].filter(
        Boolean
      )
    }
  ];

  return (
    <Box
      className={`trip-import-summary${
        isReopened ? ' trip-import-summary--reopened' : ''
      }`}
    >
      <Box className='trip-import-summary-header'>
        <Typography variant='body2' className='trip-import-summary-subtitle'>
          {subtitle}
        </Typography>
      </Box>

      <Box className='trip-import-summary-scroll'>
        <Box className='trip-import-summary-cards'>
          {cards.map(({ key, Icon, label, headline, sublines }) => (
            <Box
              key={key}
              className={`trip-import-summary-card trip-import-summary-card--${key}`}
            >
              <Box className='trip-import-summary-card-icon' aria-hidden='true'>
                <Icon size={18} weight='bold' />
              </Box>
              <Box className='trip-import-summary-card-body'>
                <Typography className='trip-import-summary-card-label'>
                  {label}
                </Typography>
                {typeof headline === 'string' ? (
                  <Typography className='trip-import-summary-card-headline'>
                    {headline}
                  </Typography>
                ) : (
                  headline
                )}
                {sublines.map((subline, index) =>
                  typeof subline === 'string' ? (
                    <Typography
                      key={index}
                      className='trip-import-summary-card-subline'
                    >
                      {subline}
                    </Typography>
                  ) : (
                    subline
                  )
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {notes.length > 0 && (
          <Box className='trip-import-summary-section'>
            <Typography
              component='h3'
              className='trip-import-summary-section-title'
            >
              <Lightbulb
                size={18}
                className='trip-import-summary-section-icon'
              />
              {t('tripImport.summary.assumptionsTitle')}
            </Typography>
            {noteGroups.map((group) => (
              <Box
                key={group.area}
                className='trip-import-summary-assumption-group'
              >
                <Typography
                  component='h4'
                  className='trip-import-summary-assumption-group-title'
                >
                  {t(`tripImport.summary.areas.${group.area}`)}
                </Typography>
                <Box className='trip-import-summary-assumptions'>
                  {group.items.map((note, index) => {
                    const destination = getDecisionDestination(note);
                    return (
                      <Box
                        key={`${group.area}-${index}`}
                        className='trip-import-summary-assumption-row'
                      >
                        <span className='trip-import-summary-assumption-dot' />
                        <Typography
                          variant='body2'
                          className='trip-import-summary-assumption-text'
                        >
                          {note?.text}
                        </Typography>
                        {showLinks && (
                          <XGButton
                            id={`trip-import-assumption-link-${group.area}-${index}`}
                            type='tertiary'
                            variant='text'
                            size='small'
                            text={t(destination.labelKey, {
                              day: destination.day
                            })}
                            endIcon={<CaretRight size={12} />}
                            onClick={() => handleGoToDecision(note)}
                            className='trip-import-summary-assumption-link'
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {notImported.length > 0 && (
          <Box className='trip-import-summary-section'>
            <Typography
              component='h3'
              className='trip-import-summary-section-title'
            >
              <EyeSlash
                size={18}
                className='trip-import-summary-section-icon'
              />
              {t('tripImport.summary.leftOutTitle')}
            </Typography>
            <Box className='trip-import-summary-left-out'>
              {notImported.map((item, index) => (
                <XGChip
                  key={index}
                  id={`trip-import-left-out-${index}`}
                  size='small'
                  variant='filled'
                  icon={<Prohibit size={12} />}
                  text={item}
                  className='trip-import-summary-left-out-chip'
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {!isReopened && (
        <>
          <Box className='trip-import-summary-footer'>
            <Typography className='trip-import-summary-footer-note'>
              {t('tripImport.summary.footerNote')}
            </Typography>

            <Box className='trip-import-summary-actions'>
              <XGButton
                id='trip-import-discard-button'
                text={t('tripImport.summary.discard')}
                type='secondary'
                size='large'
                variant='outlined'
                onClick={() => setIsDiscardModalOpen(true)}
              />
              <XGButton
                id='trip-import-go-to-trip-button'
                text={t('tripImport.summary.goToTrip')}
                type='primary'
                size='large'
                variant='contained'
                onClick={handleGoToTrip}
              />
            </Box>
          </Box>

          <DeleteModal
            id='trip-import-discard-modal'
            isOpenModal={isDiscardModalOpen}
            title={t('tripImport.summary.discardConfirmTitle')}
            description={t('tripImport.summary.discardConfirmDescription')}
            onConfirm={handleConfirmDiscard}
            onCancel={() => setIsDiscardModalOpen(false)}
            isSubmitting={isDiscarding}
          />
        </>
      )}
    </Box>
  );
};

TripImportSummary.propTypes = {
  draft: PropTypes.shape({
    source: PropTypes.object,
    trip: PropTypes.object,
    amenities: PropTypes.object,
    additionalContent: PropTypes.array,
    addOns: PropTypes.array,
    pricingPlans: PropTypes.array,
    notImported: PropTypes.array,
    notes: PropTypes.array,
    counts: PropTypes.object
  }).isRequired,
  tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDiscarded: PropTypes.func,
  mode: PropTypes.oneOf(['created', 'reopened']),
  showLinks: PropTypes.bool
};

TripImportSummary.defaultProps = {
  tripId: null,
  onDiscarded: undefined,
  mode: 'created',
  showLinks: true
};

export default TripImportSummary;
